type Subset<K> = {
  [attr in keyof K]?: K[attr] extends object
    ? Subset<K[attr]>
    : K[attr] extends object | null
      ? Subset<K[attr]> | null
      : K[attr] extends object | null | undefined
        ? Subset<K[attr]> | null | undefined
        : K[attr];
};

export const mergeObjects = <T extends object> (obj1: Subset<T>, obj2: Subset<T>): T => {
  const mergedObj = { ...obj1 };

  for (const key in obj2) {
    if (typeof obj2[ key ] === 'object' && obj2[ key ] !== null) {
      if (typeof mergedObj[ key ] === 'object' && mergedObj[ key ] !== null) {
        mergedObj[ key ] = mergeObjects(
          mergedObj[ key ] as Partial<T>,
          obj2[ key ] as Partial<T>,
        ) as T[Extract<keyof T, string>];
      } else {
        mergedObj[ key ] = { ...(obj2[ key ] as object) } as T[Extract<keyof T, string>];
      }
    } else {
      mergedObj[ key ] = obj2[ key ] as T[Extract<keyof T, string>] | undefined;
    }
  }

  return mergedObj as T;
};

export const defineOwnProperties =
  <T extends object, Y extends object = Record<string, unknown>> (obj: T, props: Y): void => {
    for (const [ key, value ] of Object.entries(props)) {
      const setterName = `set${key.charAt(0).toUpperCase()}${key.slice(1)}`;
      const getterName = `get${key.charAt(0).toUpperCase()}${key.slice(1)}`;

      const methodSetter = obj[ setterName as keyof T ] as (_value: unknown) => void;
      const propertySetter = (_value: unknown): void => {
        Object.defineProperty(obj, key, { value: _value });
      };
      const setter = typeof methodSetter === 'function' ? methodSetter : propertySetter;

      if (Object.hasOwn(obj, key) || typeof obj[ getterName as keyof T ] === 'function') {
        setter(value);
      }
    }
  };
